const LONDON_CITY_SLUGS = new Set(['london', 'greater-london']);

export const LONDON_AREA_ORDER = [
  'Central London',
  'West London',
  'East London',
  'North London',
  'South London',
] as const;

export type LondonArea = (typeof LONDON_AREA_ORDER)[number];

type VenueLocationInput = {
  city?: string | null;
  neighborhood?: string | null;
  address?: string | null;
  slug?: string | null;
  description?: string | null;
};

const AREA_KEYWORDS: Record<LondonArea, string[]> = {
  'Central London': [
    'central london',
    'soho',
    'covent garden',
    'fitzrovia',
    'holborn',
    'marylebone',
    'mayfair',
    'clerkenwell',
    'westminster',
    'victoria',
    'belgravia',
    'bloomsbury',
    'bank',
    'city of london',
    'london bridge',
    'borough',
    'south bank',
    'farringdon',
  ],
  'West London': [
    'west london',
    'chiswick',
    'kensington',
    'chelsea',
    'notting hill',
    'hammersmith',
    'fulham',
    'paddington',
    'acton',
    'ealing',
    'richmond',
    'shepherds bush',
    "shepherd's bush",
    'westbourne grove',
  ],
  'East London': [
    'east london',
    'hackney',
    'shoreditch',
    'canary wharf',
    'stratford',
    'bethnal green',
    'dalston',
    'whitechapel',
    'bow',
    'wapping',
    'poplar',
    'mile end',
  ],
  'North London': [
    'north london',
    'angel',
    'islington',
    'camden',
    'wood green',
    'highbury',
    'hampstead',
    'finsbury park',
    'archway',
    'muswell hill',
    'crouch end',
  ],
  'South London': [
    'south london',
    'vauxhall',
    'wandsworth',
    'brixton',
    'clapham',
    'camberwell',
    'peckham',
    'greenwich',
    'dulwich',
    'battersea',
    'putney',
    'wimbledon',
    'balham',
    'tooting',
  ],
};

const BEST_FOR_PATTERNS: Array<{ patterns: string[]; label: string }> = [
  { patterns: ['performance & athlete focus', 'athlete'], label: 'Best for athletes' },
  { patterns: ['good for beginners', 'beginner', 'first-timer'], label: 'Best for beginners' },
  { patterns: ['clinic-grade facilities', 'clinical'], label: 'Best for clinical recovery' },
  { patterns: ['contrast therapy circuit', 'contrast'], label: 'Best for contrast therapy' },
  { patterns: ['multiple therapy types', 'multi-therapy'], label: 'Best for variety' },
  { patterns: ['private sessions'], label: 'Best for privacy' },
  { patterns: ['membership available'], label: 'Best for regulars' },
  { patterns: ['drop-in available', 'walk-in'], label: 'Best for drop-ins' },
  { patterns: ['online booking'], label: 'Best for easy booking' },
  { patterns: ['premium equipment'], label: 'Best for premium equipment' },
  { patterns: ['couples sessions'], label: 'Best for couples' },
];

function normalise(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

function toTitleCase(value: string): string {
  return value
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function cleanNeighborhood(value: string | null | undefined): string | undefined {
  const normalized = (value ?? '').trim();
  if (!normalized) return undefined;
  return toTitleCase(normalized);
}

export function isLondonCity(city?: string | null): boolean {
  const normalized = normalise(city).replace(/\s+/g, '-');
  return LONDON_CITY_SLUGS.has(normalized);
}

export function inferLondonArea(input: VenueLocationInput): LondonArea {
  const haystack = [
    input.neighborhood,
    input.address,
    input.slug?.replace(/-/g, ' '),
    input.description,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  for (const area of LONDON_AREA_ORDER) {
    if (AREA_KEYWORDS[area].some((keyword) => haystack.includes(keyword))) {
      return area;
    }
  }

  // Keep London venues in one of the five broad buckets even when the source data is sparse.
  return 'Central London';
}

export function deriveVenueLocation(input: VenueLocationInput): {
  area?: string;
  neighborhood?: string;
} {
  const neighborhood = cleanNeighborhood(input.neighborhood);

  if (!isLondonCity(input.city)) {
    return {
      area: neighborhood,
      neighborhood,
    };
  }

  return {
    area: inferLondonArea(input),
    neighborhood,
  };
}

export function deriveBestFor(
  tags: string[] = [],
  description?: string | null,
  primaryTherapy?: string | null,
): string {
  const searchable = [...tags, description ?? '', primaryTherapy ?? '']
    .join(' ')
    .toLowerCase();

  for (const rule of BEST_FOR_PATTERNS) {
    if (rule.patterns.some((pattern) => searchable.includes(pattern))) {
      return rule.label;
    }
  }

  return 'Best for recovery';
}

export function formatTherapyLabel(value: string | null | undefined): string {
  return (value ?? '')
    .replace(/[-_]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function getVenueLocationFilterLabel(area?: string | null, neighborhood?: string | null): string | null {
  const cleanNeighborhoodValue = cleanNeighborhood(neighborhood);
  if (cleanNeighborhoodValue) return cleanNeighborhoodValue;

  const cleanAreaValue = cleanNeighborhood(area);
  if (cleanAreaValue) return cleanAreaValue;

  return null;
}

export function locationLabelToParam(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^\w\s-]+/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

export function getVenueLocationOptions<T extends { area?: string | null; neighborhood?: string | null }>(
  venues: T[],
): string[] {
  const uniqueLocations = new Set<string>();

  for (const venue of venues) {
    const label = getVenueLocationFilterLabel(venue.area, venue.neighborhood);
    if (label) uniqueLocations.add(label);
  }

  return Array.from(uniqueLocations).sort((a, b) => {
    if (a === 'Various / Pop-Up') return 1;
    if (b === 'Various / Pop-Up') return -1;
    return a.localeCompare(b);
  });
}

export function formatVenueLocationLabel(area?: string | null, neighborhood?: string | null): string {
  if (neighborhood && area) return `${neighborhood}, ${area}`;
  return area || neighborhood || 'Location available on profile';
}
